import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import type { User, UserRole } from '../types'
import { MfaChallengeType } from '../types/enums'
import { STORAGE_KEYS, AUTH_CONFIG } from '../constants/config'

interface AuthContextType {
  user: User | null
  loading: boolean
  isAuthenticated: boolean
  login: (email: string, password: string, mfaCode?: string) => Promise<{ mfaRequired?: boolean }>
  logout: () => void
}

const AuthContext = createContext<AuthContextType | null>(null)

function decodeJwtPayload(token: string): Record<string, unknown> {
  try {
    const parts = token.split('.')
    if (parts.length !== 3) throw new Error('Invalid JWT format')
    const payload = parts[1]
    const padded = payload.replace(/-/g, '+').replace(/_/g, '/')
    const decoded = atob(padded)
    return JSON.parse(decoded)
  } catch {
    return {}
  }
}

function isTokenExpired(token: string): boolean {
  const payload = decodeJwtPayload(token)
  const exp = payload.exp as number | undefined
  if (!exp) return true
  return Date.now() >= exp * 1000
}

function extractUserFromToken(token: string): User | null {
  const payload = decodeJwtPayload(token)
  if (!payload.sub || !payload.email) return null

  return {
    id: payload.sub as string,
    email: payload.email as string,
    role: (payload['custom:role'] as UserRole) || AUTH_CONFIG.DEFAULT_ROLE,
    institutionId: payload['custom:institution_id'] as string | undefined,
    universityId: payload['custom:university_id'] as string | undefined,
    preferredTaxonomy: payload['custom:preferred_taxonomy'] as string | undefined,
    preferredNav: payload['custom:preferred_nav'] as User['preferredNav'] | undefined,
  }
}

const TOKEN_KEYS = {
  idToken: STORAGE_KEYS.ID_TOKEN,
  accessToken: STORAGE_KEYS.ACCESS_TOKEN,
  refreshToken: STORAGE_KEYS.REFRESH_TOKEN,
} as const

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)

  // On mount, check for existing valid token
  useEffect(() => {
    const idToken = localStorage.getItem(TOKEN_KEYS.idToken)
    if (idToken && !isTokenExpired(idToken)) {
      const extractedUser = extractUserFromToken(idToken)
      setUser(extractedUser)
    } else if (idToken) {
      // Token expired - clear all tokens
      localStorage.removeItem(TOKEN_KEYS.idToken)
      localStorage.removeItem(TOKEN_KEYS.accessToken)
      localStorage.removeItem(TOKEN_KEYS.refreshToken)
    }
    setLoading(false)
  }, [])

  const login = useCallback(async (email: string, password: string, mfaCode?: string): Promise<{ mfaRequired?: boolean }> => {
    const config = window.ACAD_DISCOVERY_CONFIG

    const authParams: Record<string, string> = {
      USERNAME: email,
      PASSWORD: password,
    }

    if (mfaCode) {
      authParams.SMS_MFA_CODE = mfaCode
    }

    const response = await fetch(`https://cognito-idp.${config.REGION}.amazonaws.com/`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-amz-json-1.1',
        'X-Amz-Target': mfaCode
          ? 'AWSCognitoIdentityProviderService.RespondToAuthChallenge'
          : 'AWSCognitoIdentityProviderService.InitiateAuth',
      },
      body: JSON.stringify(
        mfaCode
          ? {
              ChallengeName: MfaChallengeType.SMS_MFA,
              ClientId: config.COGNITO_CLIENT_ID,
              ChallengeResponses: authParams,
            }
          : {
              AuthFlow: 'USER_PASSWORD_AUTH',
              ClientId: config.COGNITO_CLIENT_ID,
              AuthParameters: authParams,
            }
      ),
    })

    const data = await response.json()

    if (!response.ok) {
      const message = data.message || data.__type || 'Authentication failed'
      throw new Error(message)
    }

    // MFA challenge returned
    if (data.ChallengeName === MfaChallengeType.SMS_MFA || data.ChallengeName === MfaChallengeType.SOFTWARE_TOKEN_MFA) {
      return { mfaRequired: true }
    }

    const authResult = data.AuthenticationResult
    if (!authResult) {
      throw new Error('No authentication result received')
    }

    // Store tokens
    localStorage.setItem(TOKEN_KEYS.idToken, authResult.IdToken)
    localStorage.setItem(TOKEN_KEYS.accessToken, authResult.AccessToken)
    if (authResult.RefreshToken) {
      localStorage.setItem(TOKEN_KEYS.refreshToken, authResult.RefreshToken)
    }

    // Extract user from token
    const extractedUser = extractUserFromToken(authResult.IdToken)
    setUser(extractedUser)

    return {}
  }, [])

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEYS.idToken)
    localStorage.removeItem(TOKEN_KEYS.accessToken)
    localStorage.removeItem(TOKEN_KEYS.refreshToken)
    setUser(null)
  }, [])

  const value: AuthContextType = {
    user,
    loading,
    isAuthenticated: !!user,
    login,
    logout,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
